import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  decideArticleIdentityReviewRow,
  fetchArticleIdentityReviewRows,
  fetchArticleIdentityReviewSummary,
} from "../api/articleIdentityReview";
import type {
  ArticleIdentityReviewDecisionAction,
  ArticleIdentityReviewRow,
  ArticleIdentityReviewRowsResponse,
  ArticleIdentityReviewSource,
  ArticleIdentityReviewStatus,
  ArticleIdentityReviewSummary,
} from "../api/articleIdentityReview";
import { useSession } from "../auth/useSession";
import {
  FilterToolbar,
  TableEmptyRow,
  TableErrorRow,
  TableHeadCell,
  TableLoadMoreFooter,
  TableLoadingRow,
  TablePanel,
} from "../components/DataTable";
import { canUseLegacyIdentityReview } from "../lib/role";
import KpiCard from "../components/KpiCard";
import SearchInput from "../components/SearchInput";
import SegmentedControl from "../components/SegmentedControl";
import { SourceCoveragePills, formatCoverageCount } from "../components/SourceCoverage";

type ReviewSourceFilter = "all" | ArticleIdentityReviewSource;
type ReviewStatusFilter = "all" | ArticleIdentityReviewStatus;

const PAGE_SIZE = 100;

const SOURCE_OPTIONS: Array<{ value: ReviewSourceFilter; label: string }> = [
  { value: "all", label: "Alle" },
  { value: "weak_mfr", label: "Hersteller-Code" },
  { value: "ean_risk", label: "EAN-Risiko" },
  { value: "generic_sht", label: "SHT-Sammelcodes" },
];

const STATUS_OPTIONS: Array<{ value: ReviewStatusFilter; label: string }> = [
  { value: "open", label: "Offen" },
  { value: "confirmed", label: "Geprüft" },
  { value: "rejected", label: "Abgelehnt" },
  { value: "all", label: "Alle Status" },
];

// Lokales Diagnose-Werkzeug mit internen Schlüsseln/Kandidaten (#104): nur
// solange der Server den bewusst legacy-only Pfad freigibt. Der Gate-Wrapper
// hält die Hooks der Inhalts-Komponente aus dem bedingten Zweig heraus.
export default function ArticleIdentityReviewPage() {
  const { user } = useSession();

  if (!canUseLegacyIdentityReview(user)) {
    return (
      <main className="mx-auto max-w-7xl px-6 py-6">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-950">
          Identitätsprüfung
        </h1>
        <p className="mt-3 max-w-xl rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Diese lokale Diagnose ist für Ihren aktuellen Zugriff nicht
          verfügbar.
        </p>
      </main>
    );
  }

  return <ArticleIdentityReviewContent />;
}

function ArticleIdentityReviewContent() {
  const queryClient = useQueryClient();
  const [sourceFilter, setSourceFilter] = useState<ReviewSourceFilter>("all");
  const [statusFilter, setStatusFilter] = useState<ReviewStatusFilter>("open");
  const [search, setSearch] = useState("");
  const [decisionError, setDecisionError] = useState<{
    reviewId: string;
    message: string;
  } | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const searchText = search.trim();
  const summaryQuery = useQuery({
    queryKey: ["article-identity-review", "current", "summary"],
    queryFn: fetchArticleIdentityReviewSummary,
    staleTime: 60_000,
  });
  const rowsQuery = useInfiniteQuery({
    queryKey: [
      "article-identity-review",
      "current",
      "rows",
      sourceFilter,
      statusFilter,
      searchText,
    ],
    queryFn: ({ pageParam }) =>
      fetchArticleIdentityReviewRows({
        source: sourceFilter === "all" ? null : sourceFilter,
        status: statusFilter === "all" ? null : statusFilter,
        search: searchText || null,
        limit: PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage: ArticleIdentityReviewRowsResponse) => {
      const nextOffset = lastPage.offset + lastPage.items.length;
      if (lastPage.items.length < lastPage.limit) return undefined;
      if (nextOffset >= lastPage.total) return undefined;
      return nextOffset;
    },
    staleTime: 30_000,
  });

  const decisionMutation = useMutation({
    mutationFn: decideArticleIdentityReviewRow,
    onSuccess: () => {
      setDecisionError(null);
      void queryClient.invalidateQueries({
        queryKey: ["article-identity-review"],
      });
    },
    onError: (error: Error, variables) => {
      setDecisionError({
        reviewId: variables.reviewId,
        message: error.message,
      });
    },
  });

  const isRefreshing = rowsQuery.isFetching && !rowsQuery.isFetchingNextPage;
  const items = useMemo(
    () =>
      isRefreshing
        ? []
        : (rowsQuery.data?.pages.flatMap((page) => page.items) ?? []),
    [isRefreshing, rowsQuery.data],
  );
  const firstPage = rowsQuery.data?.pages[0];
  const total = firstPage?.total ?? null;
  const filteredSummary = firstPage?.filtered_summary;
  const hasMoreRows = Boolean(rowsQuery.hasNextPage);
  const loadMoreRows = useCallback(() => {
    if (!rowsQuery.hasNextPage || rowsQuery.isFetchingNextPage) return;
    void rowsQuery.fetchNextPage();
  }, [rowsQuery]);

  useEffect(() => {
    if (!hasMoreRows) return;
    const target = loadMoreRef.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadMoreRows();
        }
      },
      { rootMargin: "280px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMoreRows, loadMoreRows]);

  const pendingReviewId = decisionMutation.isPending
    ? decisionMutation.variables?.reviewId
    : null;

  function decide(row: ArticleIdentityReviewRow, action: ArticleIdentityReviewDecisionAction) {
    decisionMutation.mutate({ reviewId: row.review_id, action });
  }

  return (
    <main className="mx-auto max-w-7xl px-6 py-6">
      <header className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-950">
              Identitätsprüfung
            </h1>
            <span className="rounded border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
              Diagnose
            </span>
          </div>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            Artikel-Kandidaten mit schwacher oder mehrdeutiger Identität aus
            lokalen Prüfartefakten.
          </p>
        </div>
        <div className="rounded border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">
          Run
          <div className="font-medium text-slate-800">
            {summaryQuery.data?.run_id ?? firstPage?.run_id ?? "–"}
          </div>
        </div>
      </header>

      <section
        aria-label="Kennzahlen"
        className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5"
      >
        <KpiCard
          label="Kandidaten"
          value={formatCoverageCount(summaryQuery.data?.total_rows)}
        />
        <KpiCard
          label="Offen"
          value={formatCoverageCount(
            summaryCount(summaryQuery.data?.summary, "review_status", "open"),
          )}
          tone="negative"
        />
        <KpiCard
          label="Geprüft"
          value={formatCoverageCount(
            summaryCount(
              summaryQuery.data?.summary,
              "review_status",
              "confirmed",
            ),
          )}
          tone="positive"
        />
        <KpiCard
          label="Abgelehnt"
          value={formatCoverageCount(
            summaryCount(
              summaryQuery.data?.summary,
              "review_status",
              "rejected",
            ),
          )}
        />
        <KpiCard
          label="Entscheidungen"
          value={formatCoverageCount(summaryQuery.data?.decision_event_count)}
        />
      </section>

      <FilterToolbar>
        <SegmentedControl
          value={sourceFilter}
          options={SOURCE_OPTIONS}
          onChange={setSourceFilter}
          ariaLabel="Review-Typ filtern"
          label="Typ"
        />
        <SegmentedControl
          value={statusFilter}
          options={STATUS_OPTIONS}
          onChange={setStatusFilter}
          ariaLabel="Review-Status filtern"
          label="Status"
        />
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="EAN, Artikelcode oder Hersteller suchen…"
          ariaLabel="Identitätsprüfung suchen"
          className="sm:min-w-[18rem]"
        />
      </FilterToolbar>

      <TablePanel
        title="Kandidaten"
        subtitle={
          <span>
            {formatCoverageCount(total)} Treffer
            {filteredSummary
              ? ` · ${formatCoverageCount(
                  summaryCount(filteredSummary, "review_source", "ean_risk"),
                )} EAN-Risiken`
              : ""}
          </span>
        }
        footer={
          rowsQuery.isLoading || rowsQuery.isError || isRefreshing ? null : (
            <TableLoadMoreFooter
              loadMoreRef={loadMoreRef}
              loadedCount={items.length}
              totalCount={total}
              itemLabel="Kandidaten"
              hasMore={hasMoreRows}
              isLoading={rowsQuery.isFetchingNextPage}
              buttonLabel="Weitere Kandidaten laden"
              loadingLabel="Lade weitere Kandidaten …"
              onLoadMore={loadMoreRows}
            />
          )
        }
      >
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <TableHeadCell>Kandidat</TableHeadCell>
              <TableHeadCell>Status</TableHeadCell>
              <TableHeadCell>Schlüssel</TableHeadCell>
              <TableHeadCell>Nachweise</TableHeadCell>
              <TableHeadCell>Grund</TableHeadCell>
              <TableHeadCell>Aktion</TableHeadCell>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rowsQuery.isLoading || isRefreshing ? (
              <TableLoadingRow
                colSpan={6}
                label={`Lade ${sourceFilterLabel(sourceFilter)}...`}
              />
            ) : rowsQuery.isError ? (
              <TableErrorRow colSpan={6}>
                Identitätsprüfung konnte nicht geladen werden.
              </TableErrorRow>
            ) : items.length === 0 ? (
              <TableEmptyRow colSpan={6}>
                Keine Kandidaten für die aktuelle Auswahl.
              </TableEmptyRow>
            ) : (
              items.map((row) => (
                <IdentityReviewRow
                  key={row.review_id}
                  row={row}
                  isPending={pendingReviewId === row.review_id}
                  errorMessage={
                    decisionError?.reviewId === row.review_id
                      ? decisionError.message
                      : null
                  }
                  onDecide={decide}
                />
              ))
            )}
          </tbody>
        </table>
      </TablePanel>
    </main>
  );
}

function IdentityReviewRow({
  row,
  isPending,
  errorMessage,
  onDecide,
}: {
  row: ArticleIdentityReviewRow;
  isPending: boolean;
  errorMessage: string | null;
  onDecide: (
    row: ArticleIdentityReviewRow,
    action: ArticleIdentityReviewDecisionAction,
  ) => void;
}) {
  const articleKey = row.article_group_key || firstToken(row.article_group_key_samples);
  return (
    <tr data-review-id={row.review_id} className="hover:bg-slate-50">
      <td className="px-3 py-2 align-top">
        <div className="flex flex-col gap-1">
          <SourceCoveragePills
            emptyLabel=""
            sources={[
              {
                key: row.review_source,
                label: sourceFilterLabel(row.review_source),
                active: true,
                tone: row.review_source === "ean_risk" ? "review" : "identity",
              },
            ]}
          />
          <span className="break-all font-mono text-xs text-slate-600">
            {row.review_id}
          </span>
        </div>
      </td>
      <td className="px-3 py-2 align-top">
        <ReviewStatusBadge status={row.review_status} />
        {row.decision_created_at ? (
          <div className="mt-1 text-xs text-slate-400">
            {row.decision_created_at.slice(0, 10)}
          </div>
        ) : null}
      </td>
      <td className="max-w-xs px-3 py-2 align-top text-xs text-slate-600">
        <div className="space-y-1">
          <KeyValue label="EAN" value={row.normalized_ean} />
          <KeyValue
            label="Hersteller-Code"
            value={row.normalized_manufacturer_article_code}
          />
          {articleKey ? (
            <div>
              <span className="text-slate-400">Artikel </span>
              <Link
                to={`/articles/${encodeURIComponent(articleKey)}`}
                className="font-mono text-slate-800 underline-offset-2 hover:underline"
              >
                {articleKey}
              </Link>
            </div>
          ) : null}
          <KeyValue label="GC" value={compactJoin(row.gc_article_code, row.gc_uom)} />
          <KeyValue label="SHT" value={compactJoin(row.sht_article_code, row.sht_uom)} />
        </div>
      </td>
      <td className="max-w-sm px-3 py-2 align-top text-xs text-slate-600">
        <div className="space-y-1">
          <KeyValue label="Großhändler" value={row.suppliers} />
          <KeyValue label="Artikelcodes" value={row.article_code_samples} />
          <KeyValue label="Hersteller" value={row.manufacturer_name_samples} />
          <KeyValue label="Warengruppe" value={row.product_group_samples} />
        </div>
      </td>
      <td className="max-w-xs px-3 py-2 align-top">
        <ReasonPills row={row} />
      </td>
      <td className="px-3 py-2 align-top">
        <IdentityDecisionActions
          row={row}
          isPending={isPending}
          errorMessage={errorMessage}
          onDecide={onDecide}
        />
      </td>
    </tr>
  );
}

function IdentityDecisionActions({
  row,
  isPending,
  errorMessage,
  onDecide,
}: {
  row: ArticleIdentityReviewRow;
  isPending: boolean;
  errorMessage: string | null;
  onDecide: (
    row: ArticleIdentityReviewRow,
    action: ArticleIdentityReviewDecisionAction,
  ) => void;
}) {
  if (row.review_status === "confirmed" || row.review_status === "rejected") {
    return (
      <div className="flex min-h-9 flex-col justify-center gap-0.5">
        <span
          className={
            row.review_status === "confirmed"
              ? "text-xs font-medium text-emerald-700"
              : "text-xs font-medium text-rose-700"
          }
        >
          {reviewStatusLabel(row.review_status)}
        </span>
        <button
          type="button"
          disabled={isPending}
          onClick={() => onDecide(row, "reset")}
          className="w-fit text-xs font-medium text-slate-500 underline-offset-2 hover:text-slate-800 hover:underline disabled:cursor-default disabled:opacity-60"
        >
          {isPending ? "Speichert…" : "Zurücksetzen"}
        </button>
        {errorMessage ? <span className="text-xs text-rose-600">{errorMessage}</span> : null}
      </div>
    );
  }

  return (
    <div className="flex min-h-9 flex-col gap-1">
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          disabled={isPending}
          onClick={() => onDecide(row, "confirm")}
          className="inline-flex h-8 items-center gap-1 rounded-md bg-emerald-600 px-2 text-xs font-medium text-white shadow-sm hover:bg-emerald-700 disabled:cursor-default disabled:opacity-60"
        >
          {isPending ? "Speichert…" : "Als geprüft markieren"}
        </button>
        <button
          type="button"
          disabled={isPending}
          onClick={() => onDecide(row, "reject")}
          className="inline-flex h-8 items-center gap-1 rounded-md border border-rose-200 bg-white px-2 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:cursor-default disabled:opacity-60"
        >
          Ablehnen
        </button>
      </div>
      {errorMessage ? <span className="text-xs text-rose-600">{errorMessage}</span> : null}
    </div>
  );
}

function ReviewStatusBadge({ status }: { status: string }) {
  const style =
    status === "confirmed"
      ? "bg-emerald-50 text-emerald-800"
      : status === "rejected"
        ? "bg-rose-50 text-rose-700"
        : "bg-amber-50 text-amber-800";
  return (
    <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${style}`}>
      {reviewStatusLabel(status)}
    </span>
  );
}

function ReasonPills({ row }: { row: ArticleIdentityReviewRow }) {
  const codes = splitReviewCodes(
    row.review_bucket || row.review_reason || row.risk_reasons,
  );
  if (codes.length === 0) return <span className="text-xs text-slate-400">–</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {codes.map((code) => (
        <span
          key={code}
          className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600"
        >
          {reviewReasonLabel(code)}
        </span>
      ))}
    </div>
  );
}

function KeyValue({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="truncate" title={value}>
      <span className="text-slate-400">{label} </span>
      <span>{value}</span>
    </div>
  );
}

function summaryCount(
  summary: ArticleIdentityReviewSummary | null | undefined,
  column: string,
  value: string,
): number {
  return summary?.counts?.[column]?.[value] ?? 0;
}

function splitReviewCodes(value?: string | null): string[] {
  if (!value) return [];
  return value
    .split(/[|,]/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function sourceFilterLabel(source: string): string {
  if (source === "weak_mfr") return "Hersteller-Code";
  if (source === "ean_risk") return "EAN-Risiko";
  if (source === "generic_sht") return "SHT-Sammelcodes";
  return "Alle Kandidaten";
}

function reviewStatusLabel(status: string): string {
  if (status === "confirmed") return "Geprüft";
  if (status === "rejected") return "Abgelehnt";
  return "Offen";
}

function reviewReasonLabel(code: string): string {
  const labels: Record<string, string> = {
    ean_fan_out_multiple_group_keys: "EAN mit mehreren Artikeln",
    generic_sht_code_multiple_manufacturers: "SHT-Code mit mehreren Herstellern",
    weak_mfr_missing_gc_name: "Herstellername fehlt",
    uom_mismatch_same_ean: "Einheit weicht ab",
    mfr_code_conflict_same_ean: "Hersteller-Code widersprüchlich",
  };
  return labels[code] ?? code;
}

function compactJoin(...parts: Array<string | null | undefined>): string | null {
  const values = parts.filter(Boolean);
  return values.length ? values.join(" · ") : null;
}

function firstToken(value?: string | null): string | null {
  if (!value) return null;
  return value.split(/[,\s]+/).find(Boolean) ?? null;
}
